import styles from './index.module.css'

import { RxClock } from '@react-icons/all-files/rx/RxClock'
import { RxExternalLink } from '@react-icons/all-files/rx/RxExternalLink'
import { RxPencil1 } from '@react-icons/all-files/rx/RxPencil1'
import cn from 'clsx'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { getBrief } from '@disco/state-management/reducers/entities/briefs'
import { getConstanceSettings } from '@disco/state-management/selectors/user'
import { formatDateWithTimezone } from '@disco/utils/time-utils'

import type { ExplorerPane } from 'types/explorer'

import {
  AccessibleIcon,
  Badge,
  Box,
  Flex,
  IconButton,
  Text,
} from 'components/ds'
import TextOverflow from 'components/ui/text-overflow'
import Tooltip from 'components/ui/tooltip'

import { useSelector } from 'hooks/use-redux'

import BriefStatusBadge from '../brief-status'
import BriefActionMenu from '../brief-action-menu'

type BriefInboxPaneHeaderProps = {
  pane: ExplorerPane
}

export const BriefInboxPaneHeader = ({ pane }: BriefInboxPaneHeaderProps) => {
  const navigate = useNavigate()

  const { briefId } = pane.meta || {}

  const brief = useSelector(state => getBrief(state, { briefId }))
  const constanceSettings = useSelector(getConstanceSettings)
  const briefWorkflowV2Enabled = constanceSettings?.BRIEF_WORKFLOW_V2 || false

  const {
    deadline,
    deadline_timezone: deadlineTimezone,
    publishing_status: publishingStatus,
    submission_status: submissionStatus,
    response_count: submissions,
    name,
  } = brief || {}

  const handleEditBrief = (briefId: string) => {
    if (briefId) {
      const url = briefWorkflowV2Enabled
        ? `/brief-editor-v2/${briefId}`
        : `/brief-editor/${briefId}/content`
      navigate(url)
    }
  }

  const timezone = deadlineTimezone
    ? formatDateWithTimezone(deadline, deadlineTimezone)
    : ''

  const actionMenu = brief && <BriefActionMenu brief={brief} />

  const deadlineBadge = deadline ? (
    <Badge color="gray" variant="soft">
      <>
        <RxClock />
        {timezone}
      </>
    </Badge>
  ) : null

  const submissionsBadge = submissions ? (
    <Badge color="gray" variant="soft">
      {`${submissions} submission${submissions > 1 ? 's' : ''}`}
    </Badge>
  ) : null

  return (
    <div className={cn('ExplorerPane_Head', styles.header)}>
      <Flex gap="1" direction="column" width="100%">
        <Flex
          pb="1"
          mr="2"
          gap="2"
          align="center"
          className={styles.headerTitleWrapper}
          minWidth="0"
        >
          <Box minWidth="0">
            <Text size="2" weight="medium">
              <TextOverflow content={name} />
            </Text>
          </Box>
          <Flex ml="auto" pt="0.5" gap="4">
            <Tooltip content="Edit brief">
              <IconButton
                variant="ghost"
                color="gray"
                size="2"
                onClick={() => handleEditBrief(briefId)}
              >
                <AccessibleIcon label="Edit Brief">
                  <RxPencil1 size={24} />
                </AccessibleIcon>
              </IconButton>
            </Tooltip>

            <Tooltip content="View brief">
              <IconButton
                variant="ghost"
                color="gray"
                size="2"
                onClick={() =>
                  window.open(
                    `/brief-editor/${briefId}/${pane.groupId}/preview`,
                    '_blank',
                  )
                }
              >
                <AccessibleIcon label="View Brief">
                  <RxExternalLink size={24} />
                </AccessibleIcon>
              </IconButton>
            </Tooltip>
            {actionMenu}
          </Flex>
        </Flex>
        <Flex align="center" gap="1" wrap="wrap">
          <BriefStatusBadge
            submissionStatus={submissionStatus}
            publishingStatus={publishingStatus}
          />
          {deadlineBadge}
          {submissionsBadge}
        </Flex>
      </Flex>
    </div>
  )
}
